import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PrintIcon from "@mui/icons-material/Print";

import Spinner from "@components/Spinner";
import { fetchInvoiceView } from "@service/arInvoiceApiService";

interface Party {
  name: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  phone: string | null;
  // Plant abbreviation — drives the printed logo file "<abbreviation>.png".
  abbreviation?: string | null;
}

interface InvoiceItem {
  project: string;
  description: string;
  qty: number;
  unit_price: string;
  total: string;
}

interface InvoiceView {
  company: Party | null;
  client: Party | null;
  inv_number: string;
  status?: string;
  voided?: boolean;
  invoice_date: string;
  billed_week: string;
  items: InvoiceItem[];
  contractor_count: number;
  sub_total: string;
  tax: string;
  shipping: string;
  other: string;
  total: string;
}

const PRINT_ROOT_ID = "ar-invoice-print-root";
const PRINT_STYLE_ID = "ar-invoice-print-style";

function cityStateZip(p: Party | null): string {
  if (!p) return "";
  const cs = [p.city, p.state].filter(Boolean).join(", ");
  return [cs, p.zip].filter(Boolean).join(" ");
}

const GREY = "#7f7f7f";

export function InvoicePreviewModal({
  invoiceId,
  onClose,
}: {
  invoiceId: number;
  onClose: () => void;
}) {
  const [data, setData] = useState<InvoiceView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isVoided =
    !!data &&
    (data.voided === true || (data.status || "").toLowerCase() === "voided");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchInvoiceView(invoiceId)
      .then((res) => {
        if (!cancelled) setData(res.data as InvoiceView);
      })
      .catch((err: any) => {
        if (!cancelled)
          setError(err?.response?.data?.error || "Failed to load invoice");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [invoiceId]);

  // Print just the invoice root (browser "Save as PDF" target). Mirrors the
  // checks-print approach: hide everything else via an injected @media print
  // rule, print, then clean up.
  const handlePrint = () => {
    const style = document.createElement("style");
    style.id = PRINT_STYLE_ID;
    style.textContent = `
      @page { size: letter; margin: 0.5in; }
      @media print {
        html, body { margin: 0 !important; padding: 0 !important; height: auto !important; }
        body * { visibility: hidden; }
        #${PRINT_ROOT_ID}, #${PRINT_ROOT_ID} * { visibility: visible; }
        #${PRINT_ROOT_ID} {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          /* The @page margin already provides the page inset; the on-screen
             padding here would stack on top of it and push content onto a
             blank second page. */
          padding: 0 !important;
          overflow: hidden;
        }
      }
    `;
    document.head.appendChild(style);

    // The browser's "Save as PDF" suggests a filename based on document.title,
    // so swap it for the duration of the print and restore it afterwards.
    const prevTitle = document.title;
    const fileName = ["Invoice", data?.client?.abbreviation, data?.inv_number]
      .filter(Boolean)
      .join("_")
      .replace(/[\\/:*?"<>|]+/g, "-");
    if (fileName) document.title = fileName;

    const cleanup = () => {
      document.getElementById(PRINT_STYLE_ID)?.remove();
      document.title = prevTitle;
      window.removeEventListener("afterprint", cleanup);
    };
    window.addEventListener("afterprint", cleanup);
    window.print();
  };

  // Pad the items table to ~7 rows for a consistent form height.
  const fillerCount = data ? Math.max(0, 7 - data.items.length) : 0;

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogContent sx={{ p: 0, bgcolor: "#fff" }}>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ position: "absolute", top: 6, right: 6, zIndex: 2 }}
        >
          <CloseIcon />
        </IconButton>

        {loading && (
          <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-20">
            <Spinner className="tw-w-8 tw-h-8" />
            <span className="tw-mt-2 tw-text-sm">Loading invoice...</span>
          </div>
        )}

        {!loading && error && (
          <div className="tw-text-center tw-py-10 tw-text-red-600">{error}</div>
        )}

        {!loading && data && (
          <div
            id={PRINT_ROOT_ID}
            style={{
              position: "relative",
              fontFamily: "Arial, Helvetica, sans-serif",
              fontSize: 11,
              color: "#000",
              padding: "24px 28px",
            }}
          >
            {isVoided && (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  pointerEvents: "none",
                  zIndex: 1,
                }}
              >
                <div
                  style={{
                    border: "8px solid rgba(184, 28, 28, 0.35)",
                    color: "rgba(184, 28, 28, 0.35)",
                    fontSize: 64,
                    fontWeight: 800,
                    letterSpacing: 10,
                    padding: "14px 42px",
                    transform: "rotate(-24deg)",
                    userSelect: "none",
                    textTransform: "uppercase",
                  }}
                >
                  Voided
                </div>
              </div>
            )}

            {/* Header */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: 20,
                    fontWeight: "bold",
                    color: "#9a9a9a",
                    letterSpacing: 1,
                  }}
                >
                  PROJECT BILLING
                </div>
                <div
                  style={{ fontSize: 10, color: "#9a9a9a", fontWeight: "bold" }}
                >
                  FROM
                </div>
                <div style={{ fontWeight: "bold", fontSize: 12 }}>
                  {data.company?.name || "BUILDINGS & BUILDERS COMPANY"}
                </div>
                <div style={{ fontSize: 12 }}>
                  {data.company?.address || "8840 Whitney Drive"}
                </div>
                <div style={{ fontSize: 12 }}>
                  {cityStateZip(data.company) || "Columbus, Ohio 43035"}
                </div>
                <div style={{ fontSize: 12 }}>
                  Phone: {data.company?.phone || "(202)-924-1051"}
                </div>
              </div>

              <div style={{ textAlign: "center" }}>
                <img
                  src="/logo.png"
                  alt="Buildings & Builders"
                  style={{ height: 40 }}
                />
              </div>

              <div>
                <div
                  style={{
                    fontSize: 26,
                    fontWeight: "bold",
                    color: "#9a9a9a",
                    letterSpacing: 1,
                    textAlign: "right",
                  }}
                >
                  INVOICE
                </div>
                <table
                  style={{ borderCollapse: "collapse", marginLeft: "auto" }}
                >
                  <tbody>
                    {[
                      ["DATE", data.invoice_date],
                      ["BILLED WEEK", data.billed_week],
                      ["INVOICE #", data.inv_number],
                    ].map(([label, value]) => (
                      <tr key={label}>
                        <td style={{ padding: "1px 6px", textAlign: "right" }}>
                          {label}
                        </td>
                        <td
                          style={{
                            padding: "1px 6px",
                            border: "1px solid #000",
                            minWidth: 90,
                            textAlign: "center",
                          }}
                        >
                          {value}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div
                  style={{
                    textAlign: "center",
                    fontWeight: "bold",
                    textDecoration: "underline",
                    marginTop: 6,
                  }}
                >
                  DUE UPON RECEIPT
                </div>
              </div>
            </div>

            {/* TO — the bill-to party comes from the plant's invoicing fields
                (company_name / street_address / city / state / zip_code /
                phone_number), served by the backend under `client`. */}
            <div
              style={{
                background: "#b7b7b7",
                color: "#fff",
                fontWeight: "bold",
                padding: "2px 6px",
                marginTop: 10,
              }}
            >
              TO
            </div>
            <div
              style={{
                marginTop: 6,
                minHeight: 64,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: 16,
              }}
            >
              <div>
                <div style={{ fontWeight: "bold", fontSize: 12 }}>
                  {data.client?.name || ""}
                </div>
                {data.client && (
                  <>
                    <div style={{ fontSize: 12 }}>
                      {data.client.address || ""}
                    </div>
                    <div style={{ fontSize: 12 }}>
                      {cityStateZip(data.client)}
                    </div>
                    {data.client.phone && (
                      <div style={{ fontSize: 12 }}>
                        Phone: {data.client.phone}
                      </div>
                    )}
                  </>
                )}
              </div>
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                {data.client?.abbreviation && (
                  <img
                    src={`/${data.client.abbreviation}.png`}
                    alt="Client logo"
                    style={{
                      height: 56,
                      objectFit: "contain",
                      transform: "translate(-50px, 14px)",
                    }}
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).style.display =
                        "none";
                    }}
                  />
                )}
              </div>
            </div>

            {/* Items */}
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                marginTop: 14,
              }}
            >
              <thead>
                <tr>
                  {[
                    ["PROJECT", "18%"],
                    ["DESCRIPTION", "42%"],
                    ["QTY", "8%"],
                    ["UNIT PRICE", "16%"],
                    ["TOTAL", "16%"],
                  ].map(([label, w]) => (
                    <th
                      key={label}
                      style={{
                        background: GREY,
                        color: "#fff",
                        padding: "4px 6px",
                        border: "1px solid #000",
                        textAlign: "center",
                        width: w,
                      }}
                    >
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody style={{ borderBottom: "1px solid #000" }}>
                {data.items.map((item, idx) => (
                  <tr key={idx}>
                    <td style={cellCenter}>{item.project}</td>
                    <td style={cell}>{item.description}</td>
                    <td style={cellCenter}>{item.qty}</td>
                    <td style={cellRight}>${item.unit_price}</td>
                    <td style={cellRight}>${item.total}</td>
                  </tr>
                ))}
                {Array.from({ length: fillerCount }).map((_, idx) => (
                  <tr key={`f-${idx}`}>
                    <td style={cell}>&nbsp;</td>
                    <td style={cell} />
                    <td style={cell} />
                    <td style={cell} />
                    <td style={cell} />
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Comments + totals */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginTop: 4,
              }}
            >
              <div style={{ width: "55%" }}>
                <div
                  style={{
                    background: "#b7b7b7",
                    color: "#fff",
                    fontWeight: "bold",
                    padding: "2px 6px",
                  }}
                >
                  Comments or Special Instructions
                </div>
                <div
                  style={{
                    border: "1px solid #999",
                    borderTop: "none",
                    minHeight: 90,
                    padding: 6,
                  }}
                >
                  DETAIL REPORT IN SEPARATE ATTACHMENT.
                </div>
              </div>
              <div style={{ width: "40%" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <tbody>
                    {[
                      ["SUBTOTAL", data.sub_total],
                      ["TAX", data.tax],
                      ["SHIPPING", data.shipping],
                      ["OTHER", data.other],
                    ].map(([label, value]) => (
                      <tr key={label}>
                        <td style={{ padding: "2px 6px" }}>{label}</td>
                        <td
                          style={{
                            padding: "2px 6px",
                            textAlign: "right",
                            borderBottom: "1px solid #000",
                          }}
                        >
                          {value}
                        </td>
                      </tr>
                    ))}
                    <tr>
                      <td style={{ padding: "2px 6px", fontWeight: "bold" }}>
                        TOTAL
                      </td>
                      <td
                        style={{
                          padding: "2px 6px",
                          textAlign: "right",
                          fontWeight: "bold",
                          background: "#b8c4e8",
                          border: "1px solid #000",
                        }}
                      >
                        $&nbsp;&nbsp;{data.total}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Footer */}
            <div style={{ textAlign: "center", marginTop: 40 }}>
              <div>
                If you have any questions about this invoice, please contact
              </div>
              <div>
                <a
                  href="mailto:RECEIVABLES@BUILDINGSANDBUILDERS.COM"
                  style={{ color: "#1155cc" }}
                >
                  RECEIVABLES@BUILDINGSANDBUILDERS.COM
                </a>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>
          Close
        </Button>
        <Button
          variant="contained"
          sx={{ bgcolor: "#2642c9", textTransform: "none" }}
          startIcon={<PrintIcon />}
          onClick={handlePrint}
          disabled={loading || !data}
        >
          Print / Save as PDF
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const cell: React.CSSProperties = {
  borderLeft: "1px solid #000",
  borderRight: "1px solid #000",
  padding: "3px 6px",
  height: 18,
};
const cellCenter: React.CSSProperties = { ...cell, textAlign: "center" };
const cellRight: React.CSSProperties = { ...cell, textAlign: "right" };

export default InvoicePreviewModal;
